import { type ReactNode } from 'react';

import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';

export interface FilterPreset {
  /** Identificador do atalho. Só para a chave de render — não vai para a URL. */
  id: string;
  /** Texto do chip. */
  label: string;
  /** Ícone opcional do Lucide, já renderizado. */
  icon?: ReactNode;
  /** Combinação de filtros que o atalho aplica. Substitui a atual, não acumula. */
  values: Record<string, string>;
  /** Contagem ao lado do rótulo. Só entre se vier de dado real. */
  badge?: number;
  /** Frase longa para leitor de tela e para quem não entende o rótulo curto. */
  description?: string;
}

interface FilterPresetsProps {
  presets: readonly FilterPreset[];
  /** Valores de filtro atuais — é o que revela qual atalho está aplicado. */
  currentValues: Record<string, string>;
  onApply: (values: Record<string, string>) => void;
  label?: string;
  className?: string;
}

/**
 * Atalhos de filtro: as perguntas que a equipe faz todo dia, a um clique.
 *
 * "CRMC vencido" e "seguro vence em 30 dias" são o motivo pelo qual alguém abre a
 * tela de condutores ou de veículos. Deixar isso escondido atrás de dois seletores
 * transforma a rotina em quatro cliques e uma chance de errar o campo.
 *
 * O CHIP APLICADO SE ANUNCIA POR `aria-pressed`, e não só pela cor. Quem navega por
 * leitor de tela precisa saber que o filtro está ligado, e cor sozinha não conta
 * como informação (CLAUDE.md, "Regras de interface", item 5).
 *
 * `role="toolbar"` porque o conjunto é um grupo de controles relacionados — é o que
 * faz a tecnologia assistiva anunciar "barra de ferramentas, 4 itens" em vez de
 * quatro botões soltos no meio da página.
 */
export function FilterPresets({
  presets,
  currentValues,
  onApply,
  label = 'Atalhos',
  className,
}: FilterPresetsProps) {
  if (presets.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="toolbar"
      aria-label="Atalhos de filtro"
    >
      <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
        {label}
      </span>
      {presets.map((preset) => {
        const aplicado = estaAplicado(preset, currentValues);
        return (
          <button
            key={preset.id}
            type="button"
            onClick={() => onApply(preset.values)}
            aria-pressed={aplicado}
            aria-label={preset.description ?? preset.label}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              'focus-visible:ring-ring focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1',
              aplicado
                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {preset.icon}
            <span>{preset.label}</span>
            {typeof preset.badge === 'number' && preset.badge > 0 && (
              <Badge
                variant={aplicado ? 'secondary' : 'outline'}
                className="h-4 min-w-4 px-1 text-[10px] tabular-nums"
              >
                {preset.badge > 99 ? '99+' : preset.badge}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Aplicado quando TODAS as chaves do atalho batem com o filtro atual.
 *
 * Só as chaves do atalho, não o filtro inteiro: quem clicou em "CRMC vencido" e
 * depois digitou um nome na busca continua vendo o chip aceso — o atalho ainda
 * está valendo, apenas com um recorte a mais por cima.
 */
function estaAplicado(preset: FilterPreset, atuais: Record<string, string>): boolean {
  const pares = Object.entries(preset.values);
  if (pares.length === 0) {
    return false;
  }
  return pares.every(([chave, valor]) => (atuais[chave] ?? '') === valor);
}
